import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  intervalToDuration,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";
import { type TFunction } from "i18next";

// ponytail: keys stay under `administratorsModal` because they are already translated in every
// locale and `fallbackLng` is the user language, not English - move them to a shared namespace
// once Transifex has synced a replacement.
const RELATIVE_TIME_PREFIX = "home.manager.overview.administratorsModal.relativeTime";

/** Parse an ISO string, returning null for empty/invalid input. */
const toDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = parseISO(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

/** Absolute datetime in the design-system compact style, e.g. "19 Dec 1961, 17:45". */
export const formatDateTimeCompact = (value?: string | null): string => {
  const date = toDate(value);

  return date ? format(date, "d MMM yyyy, HH:mm") : "";
};

/** Absolute date in the design-system compact style, e.g. "19 Dec 1961". */
export const formatDateCompact = (value?: string | null): string => {
  const date = toDate(value);

  return date ? format(date, "d MMM yyyy") : "";
};

/**
 * Human-readable "time ago" for a datetime value, with minute/hour granularity for
 * recent events (e.g. "Just now", "5 minutes ago", "2 hours ago") and calendar
 * wording for older ones ("Yesterday", "3 days ago").
 */
export const formatRelativeDateTime = (value: string | null | undefined, t: TFunction): string => {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  const now = new Date();
  const seconds = differenceInSeconds(now, date);

  if (seconds < 45) {
    return t(`${RELATIVE_TIME_PREFIX}.justNow`);
  }

  const minutes = differenceInMinutes(now, date);

  if (minutes < 60) {
    return t(`${RELATIVE_TIME_PREFIX}.minutesAgo`, { count: Math.max(1, minutes) });
  }

  if (isToday(date)) {
    return t(`${RELATIVE_TIME_PREFIX}.hoursAgo`, {
      count: Math.max(1, differenceInHours(now, date)),
    });
  }

  return formatRelativeDate(value, t);
};

/**
 * Easy-reading datetime: relative wording for recent values ("Just now", "2 hours
 * ago", "Yesterday") and the compact absolute style ("19 Dec 1961, 17:45") once the
 * value is older than yesterday.
 */
export const formatFriendlyDateTime = (value: string | null | undefined, t: TFunction): string => {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  return isToday(date) || isYesterday(date)
    ? formatRelativeDateTime(value, t)
    : formatDateTimeCompact(value);
};

/**
 * Human-readable "time ago" for a date value with day granularity (e.g. "Today",
 * "Yesterday", "3 days ago"). Past a month days roll up into months and years, and the
 * two largest non-zero units are shown ("1 month, 14 days ago", "1 year, 1 month ago").
 */
export const formatRelativeDate = (value: string | null | undefined, t: TFunction): string => {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  if (isToday(date)) {
    return t(`${RELATIVE_TIME_PREFIX}.today`);
  }

  if (isYesterday(date)) {
    return t(`${RELATIVE_TIME_PREFIX}.yesterday`);
  }

  const now = new Date();
  const [start, end] = date > now ? [now, date] : [date, now];
  const { years = 0, months = 0, days = 0 } = intervalToDuration({ start, end });

  if (!years && !months) {
    return t(`${RELATIVE_TIME_PREFIX}.daysAgo`, {
      count: Math.abs(differenceInCalendarDays(now, date)),
    });
  }

  const parts: string[] = [];

  if (years) {
    parts.push(t(`${RELATIVE_TIME_PREFIX}.years`, { count: years }));
  }

  if (months) {
    parts.push(t(`${RELATIVE_TIME_PREFIX}.months`, { count: months }));
  }

  // Two units read best in a narrow cell - the absolute date is right underneath.
  if (days && parts.length < 2) {
    parts.push(t(`${RELATIVE_TIME_PREFIX}.days`, { count: days }));
  }

  return t(`${RELATIVE_TIME_PREFIX}.ago`, { duration: parts.join(", ") });
};
